import { accountsRouteTree } from 'src/navigation/accountsRouteTree'
import { AppLocation, SidenavContentType } from 'src/navigation/appLocations'
import { budgetsRouteTree } from 'src/navigation/budgetsRouteTree'
import { organizationRouteNodes } from 'src/navigation/organizationRouteTree'
import { profileRouteTree } from 'src/navigation/profileRouteTree'
import * as Routes from 'src/navigation/urls'

export type FlatAppRouteMetadata = {
  id?: AppLocation
  path: string
  contentType?: SidenavContentType
}

export type AuthenticatedFlatAppRouteMetadata = FlatAppRouteMetadata & {
  renderInAuthedShell: boolean
}

export const PricingFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  path: Routes.PRICING_ROUTE(),
  renderInAuthedShell: false,
}

export const DownloadsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  path: Routes.downloadsUrl(),
  renderInAuthedShell: false,
}

// Transactions.tsx is a bare redirect (-> /expenses/transactions or
// /mywallet/transactions, preserving query) with no chrome of its own, hence
// renderInAuthedShell: false. id/contentType are kept (unlike other
// chromeless entries above) so locationHooks still resolves this location.
export const TransactionsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.Transactions,
    path: Routes.transactionsUrl(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: false,
  }

export const ActivityFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  id: AppLocation.Activity,
  path: Routes.activityUrl(),
  contentType: SidenavContentType.Main,
  renderInAuthedShell: true,
}

export const InsightsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  id: AppLocation.Insights,
  path: Routes.insightsUrl(),
  contentType: SidenavContentType.Main,
  renderInAuthedShell: true,
}

export const VendorsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  id: AppLocation.Vendors,
  path: Routes.vendorsUrl(),
  contentType: SidenavContentType.Main,
  renderInAuthedShell: true,
}

export const StreamingAgentFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.StreamingAgent,
    path: Routes.streamingAgentUrl(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const AccountingReviewReportsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.AccountingReviewReports,
    path: Routes.accountingReviewReportsUrl(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const OnboardingFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata = {
  id: AppLocation.Onboarding,
  path: Routes.onboardingUrl(),
  contentType: SidenavContentType.Main,
  renderInAuthedShell: true,
}

export const OnboardingCardFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.OnboardingCard,
    path: Routes.onboardingCardUrl(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const OrgInviteDecisionFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.OrgInviteDecision,
    path: Routes.orgInviteDecisionUrl(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const ManagerReviewTransactionsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.ManagerReviewTransactions,
    path: Routes.managerReviewTransactionsURL(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const ManagerReviewReimbursementsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.ManagerReviewReimbursements,
    path: Routes.managerReviewReimbursementsURL(),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const ExpenseReportFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.ExpenseReport,
    path: Routes.expenseReportUrl(':id'),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const ManagerReviewReimbursementDetailFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata =
  {
    id: AppLocation.ManagerReviewReimbursementDetail,
    path: Routes.managerReviewReimbursementURL(':id'),
    contentType: SidenavContentType.Main,
    renderInAuthedShell: true,
  }

export const FAQFlatRouteMetadata: FlatAppRouteMetadata = {
  path: Routes.faqUrl(),
  contentType: SidenavContentType.Main,
}

export const TermsFlatRouteMetadata: FlatAppRouteMetadata = {
  id: AppLocation.Terms,
  path: Routes.termsUrl(),
  contentType: SidenavContentType.Main,
}

export const PrivacyFlatRouteMetadata: FlatAppRouteMetadata = {
  id: AppLocation.Privacy,
  path: Routes.privacyUrl(),
  contentType: SidenavContentType.Main,
}

export const SupportFlatRouteMetadata: FlatAppRouteMetadata = {
  id: AppLocation.Support,
  path: Routes.supportRequestUrl(),
  contentType: SidenavContentType.Main,
}

export const MarcomPricingFlatRouteMetadata: FlatAppRouteMetadata = {
  path: Routes.MARCOM_PRICING_ROUTE(),
}

// EX-39748 — Budgets tabbed family. One metadata entry per CONCRETE tab path so
// locationHooks resolves the correct AppLocation per tab (sidenav active state,
// breadcrumbs, content type). The gate (flatArchitectureRoutes.tsx) collapses
// these into one entry per family for mount-stable tab animation; metadata stays
// per-tab. All derived from the single budgetsRouteTree source.
export const BudgetsFamilyFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    {
      id: budgetsRouteTree.family.rootAppLocation,
      path: budgetsRouteTree.family.rootPath,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    },
    ...budgetsRouteTree.family.tabs.map((tab) => ({
      id: tab.appLocation,
      path: tab.path,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    })),
    ...budgetsRouteTree.family.aliasPaths.map((alias) => ({
      id: alias.appLocation,
      path: alias.path,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    })),
  ]

export const BudgetsEntityFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    {
      id: budgetsRouteTree.entity.rootAppLocation,
      path: budgetsRouteTree.entity.rootPath,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    },
    ...budgetsRouteTree.entity.tabs.map((tab) => ({
      id: tab.appLocation,
      path: tab.path,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    })),
  ]

// EX-39923 — Accounts tabbed family (supersedes #6986). One metadata entry per
// CONCRETE path so locationHooks resolves the correct AppLocation per path
// (sidenav active state, breadcrumbs, content type). The gate
// (flatArchitectureRoutes.tsx) collapses these into one entry per family so the
// page stays mounted across navigation; metadata stays per-path. All derived
// from the single accountsRouteTree source. Parity: every family path maps to
// AppLocation.Accounts (the family is one location, not per-tab).
export const AccountsFamilyFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    {
      id: accountsRouteTree.family.rootAppLocation,
      path: accountsRouteTree.family.rootPath,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    },
    ...accountsRouteTree.family.tabs.map((tab) => ({
      id: tab.appLocation,
      path: tab.path,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    })),
  ]

export const AccountsEntityFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    {
      id: accountsRouteTree.entity.rootAppLocation,
      path: accountsRouteTree.entity.rootPath,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    },
    ...accountsRouteTree.entity.tabs.map((tab) => ({
      id: tab.appLocation,
      path: tab.path,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    })),
  ]

// EX-39748 — Profile ("Profile Settings") family. One metadata entry per
// CONCRETE path so locationHooks resolves the correct AppLocation per sub-page
// (sidenav active state, breadcrumbs, content type). Legacy content-type split
// preserved: the `/profile` root stays Main (its old ProfileAppRoute.meta) while
// every sub-page stays Settings so the settings sidenav remains selected — do
// NOT collapse the sub-pages to Main. All derived from the single
// profileRouteTree source.
export const ProfileFamilyFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    {
      id: profileRouteTree.family.rootAppLocation,
      path: profileRouteTree.family.rootPath,
      contentType: SidenavContentType.Main,
      renderInAuthedShell: true,
    },
    ...profileRouteTree.family.tabs.map((tab) => ({
      id: tab.appLocation,
      path: tab.path,
      contentType: SidenavContentType.Settings,
      renderInAuthedShell: true,
    })),
  ]

// EX-39748 — Organization-settings family. Unlike Budgets/Accounts, the family
// IS the entity (everything nests under `/organizationSettings/:id`) and is
// SIDENAV-driven, so every concrete route maps to SidenavContentType.Settings
// (not Main). One metadata entry per concrete URL so locationHooks resolves the
// correct AppLocation per route (settings sidenav active state). Card Feeds
// (`/cardfeeds*`) belong to the same family. All derived from organizationRouteTree.
export const OrganizationSettingsFlatRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  organizationRouteNodes.map((node) => ({
    id: node.appLocation,
    path: node.path,
    contentType: SidenavContentType.Settings,
    renderInAuthedShell: true,
  }))

export const AuthenticatedFlatArchitectureRouteMetadata: AuthenticatedFlatAppRouteMetadata[] =
  [
    PricingFlatRouteMetadata,
    DownloadsFlatRouteMetadata,
    TransactionsFlatRouteMetadata,
    ActivityFlatRouteMetadata,
    VendorsFlatRouteMetadata,
    InsightsFlatRouteMetadata,
    StreamingAgentFlatRouteMetadata,
    AccountingReviewReportsFlatRouteMetadata,
    OnboardingFlatRouteMetadata,
    OnboardingCardFlatRouteMetadata,
    OrgInviteDecisionFlatRouteMetadata,
    ManagerReviewTransactionsFlatRouteMetadata,
    ManagerReviewReimbursementsFlatRouteMetadata,
    ExpenseReportFlatRouteMetadata,
    ManagerReviewReimbursementDetailFlatRouteMetadata,
    ...BudgetsFamilyFlatRouteMetadata,
    ...BudgetsEntityFlatRouteMetadata,
    ...AccountsFamilyFlatRouteMetadata,
    ...AccountsEntityFlatRouteMetadata,
    ...ProfileFamilyFlatRouteMetadata,
    ...OrganizationSettingsFlatRouteMetadata,
    /* HYGEN-INJECT-ROUTE-METADATA */
  ]

export const AuthenticationAgnosticFlatArchitectureRouteMetadata: FlatAppRouteMetadata[] =
  [
    FAQFlatRouteMetadata,
    TermsFlatRouteMetadata,
    PrivacyFlatRouteMetadata,
    SupportFlatRouteMetadata,
  ]

export const PublicFlatArchitectureRouteMetadata: FlatAppRouteMetadata[] = [
  MarcomPricingFlatRouteMetadata,
]
